import { z } from 'zod';
import { id, timestamp, paginationQuery } from './common.js';
import { bookingStatus } from './shift.js';

export const bookingTermsAction = z.enum([
  'request',
  'accept',
  'propose',
  'confirm',
  'accept_proposal',
]);
export type BookingTermsAction = z.infer<typeof bookingTermsAction>;
export const bookingTermsSnapshot = z.object({
  shiftId: id,
  organizationId: id,
  groupId: id,
  locationId: id,
  practiceName: z.string(),
  locationName: z.string(),
  addressLine1: z.string(),
  addressLine2: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  timezone: z.string(),
  role: z.string(),
  startsAt: timestamp,
  endsAt: timestamp,
  breakMinutes: z.number().int().nonnegative(),
  payRateCents: z.number().int().positive(),
  officeFeeBasisPoints: z.number().int().nonnegative(),
  estimatedGrossCents: z.number().int().nonnegative(),
  bookingMode: z.enum(['request', 'accept']),
  notes: z.string().nullable(),
  cancellationPolicyVersion: z.literal('dpp-cancellation-review-v1'),
  cancellationDisclosure: z.string(),
  paymentPolicyVersion: z.literal('dpp-payment-review-v1'),
  paymentDisclosure: z.string(),
});
export type BookingTermsSnapshot = z.infer<typeof bookingTermsSnapshot>;
export const bookingTermsPreview = z.object({
  action: bookingTermsAction,
  termsDigest: z.string().regex(/^[0-9a-f]{64}$/),
  terms: bookingTermsSnapshot,
  checkedAt: timestamp,
  requestIsConfirmation: z.boolean(),
});
export type BookingTermsPreview = z.infer<typeof bookingTermsPreview>;
export const bookingTermsEvent = z.object({
  id,
  action: z.enum([
    'request',
    'accept',
    'propose',
    'confirm',
    'accept_proposal',
    'decline',
    'withdraw',
  ]),
  actorUserId: id,
  actorName: z.string(),
  actorSide: z.enum(['professional', 'office']),
  recordedAt: timestamp,
  termsDigest: z.string().nullable(),
  terms: bookingTermsSnapshot.nullable(),
});
export const bookingAgreementHistory = z.object({
  bookingId: id,
  bookingStatus,
  professionalName: z.string(),
  locationName: z.string(),
  legacyNotice: z.string().nullable(),
  currentAgreedRateCents: z.number().int().nullable(),
  events: z.array(bookingTermsEvent),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(100),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});
export const bookingAgreementHistoryQuery = paginationQuery;
export type BookingAgreementHistory = z.infer<typeof bookingAgreementHistory>;
export const termsAcknowledgement = z.object({
  termsDigest: z
    .string()
    .regex(/^[0-9a-f]{64}$/, 'Review the current booking terms before continuing'),
});
export const bookWithTermsInput = termsAcknowledgement
  .extend({ kind: z.enum(['request', 'accept']) })
  .strict();
export const bookingDecisionWithTermsInput = z
  .object({
    decision: z.enum(['confirmed', 'declined']),
    termsDigest: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .optional(),
  })
  .strict()
  .refine((value) => value.decision !== 'confirmed' || value.termsDigest !== undefined, {
    message: 'Review the current booking terms before confirming',
    path: ['termsDigest'],
  });
export const proposalWithTermsInput = termsAcknowledgement.extend({ professionalId: id }).strict();
